import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ControlContainer, ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe, CurrencyPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { OfferFormService, OfferTargetAudienceFormGroup } from '@app/areas/offers/services/offer.form.service';
import { AudienceFilterType } from '@app/areas/offers/forms/offer-audience-form/offer-audience-filter.types';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { OfferAudienceTopConsumersFormComponent } from '@app/areas/offers/forms/offer-audience-form/top-consumers/offer-audience-top-consumers-form.component';
import { OfferAudienceWinBackFormComponent } from '@app/areas/offers/forms/offer-audience-form/win-back/offer-audience-win-back-form.component';
import { OfferApiService } from '@app/areas/offers/services/offer.api.service';
import { ApplicationProfileService } from '@shared/services/application-profile.service';
import { Subscription, debounceTime, distinctUntilChanged, filter, switchMap, catchError, of, take } from 'rxjs';

interface FilterCard {
  type: AudienceFilterType;
  label: string;
  description: string;
}

const FILTER_CARDS: FilterCard[] = [
  {
    type: AudienceFilterType.AllConsumers,
    label: 'All Consumers',
    description: 'Target all enrolled consumers at this location',
  },
  {
    type: AudienceFilterType.TopConsumers,
    label: 'Top Consumers',
    description: 'Highest-spending or most frequent customers',
  },
  {
    type: AudienceFilterType.WinBack,
    label: 'Win Back',
    description: 'Lapsed customers who have not purchased recently',
  },
];

@Component({
  selector: 'app-offer-audience-form',
  templateUrl: './offer-audience-form.component.html',
  styleUrls: ['./offer-audience-form.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgFor,
    NgIf,
    NgClass,
    AsyncPipe,
    CurrencyPipe,
    CardModule,
    ProgressSpinnerModule,
    OfferAudienceTopConsumersFormComponent,
    OfferAudienceWinBackFormComponent,
  ],
})
export class OfferAudienceFormComponent implements OnInit, OnDestroy {
  private readonly controlContainer = inject(ControlContainer);
  private readonly offerFormService = inject(OfferFormService);
  private readonly offerApiService = inject(OfferApiService);
  private readonly applicationProfileService = inject(ApplicationProfileService);

  private previewSub?: Subscription;
  private filterChangeSub?: Subscription;
  private initialPreviewSub?: Subscription;

  protected audienceForm: OfferTargetAudienceFormGroup | null = null;
  filterCards = FILTER_CARDS;
  protected FilterType = AudienceFilterType;

  // Preview observables for template
  previewCount$ = this.offerFormService.previewCount$;
  previewLoading$ = this.offerFormService.previewLoading$;
  previewError$ = this.offerFormService.previewError$;
  previewAverageSpend$ = this.offerFormService.previewAverageSpend$;

  ngOnInit(): void {
    this.audienceForm = this.controlContainer.control as OfferTargetAudienceFormGroup;

    // Reset preview when filter type changes (card selection/deselection)
    this.filterChangeSub = this.audienceForm.controls.FilterType.valueChanges.subscribe(() => {
      this.offerFormService.resetPreview();
    });

    // Wire preview trigger: debounce config changes, call API via switchMap
    // NFR1: 500ms client debounce; backend must respond within ~2.5s for 3-second end-to-end budget
    this.previewSub = this.audienceForm.valueChanges
      .pipe(
        debounceTime(500),
        filter(() => this.isPreviewable()),
        distinctUntilChanged((prev, curr) => this.previewKey(prev) === this.previewKey(curr)),
        switchMap(() => {
          this.offerFormService.setPreviewLoading(true);
          this.offerFormService.setPreviewError(false);
          const request = this.buildPreviewRequest();
          return this.offerApiService.getAudiencePreview(request!).pipe(
            catchError(() => {
              this.offerFormService.setPreviewError(true);
              this.offerFormService.setPreviewLoading(false);
              return of(null);
            })
          );
        })
      )
      .subscribe((response) => {
        if (response) {
          this.offerFormService.setPreviewCount(response.customerCount);
          this.offerFormService.setPreviewAverageSpend(response.averageSpend);
          this.offerFormService.setPreviewLoading(false);
        }
      });

    // Immediate preview on edit mode (pre-populated form) — skip debounce
    // Uses take(1) to auto-complete and avoids racing with the debounced pipe
    if (this.audienceForm.controls.FilterType.value && this.isPreviewable()) {
      this.offerFormService.setPreviewLoading(true);
      const request = this.buildPreviewRequest();
      if (request) {
        this.initialPreviewSub = this.offerApiService
          .getAudiencePreview(request)
          .pipe(
            take(1),
            catchError(() => {
              this.offerFormService.setPreviewError(true);
              this.offerFormService.setPreviewLoading(false);
              return of(null);
            })
          )
          .subscribe((response) => {
            if (response) {
              this.offerFormService.setPreviewCount(response.customerCount);
              this.offerFormService.setPreviewAverageSpend(response.averageSpend);
              this.offerFormService.setPreviewLoading(false);
            }
          });
      }
    }
  }

  ngOnDestroy(): void {
    this.previewSub?.unsubscribe();
    this.filterChangeSub?.unsubscribe();
    this.initialPreviewSub?.unsubscribe();
  }

  get selectedFilter(): AudienceFilterType | null {
    return this.audienceForm?.controls.FilterType.value ?? null;
  }

  get isDynamic(): boolean {
    return this.audienceForm?.controls.IsDynamicAudience.value ?? false;
  }

  // The audience is locked (read-only) when editing an existing offer — the list is fixed at creation.
  get isLocked(): boolean {
    return this.audienceForm?.controls.FilterType.disabled ?? false;
  }

  selectFilter(type: AudienceFilterType): void {
    if (!this.audienceForm || this.isLocked) return;
    const current = this.audienceForm.controls.FilterType.value;
    this.audienceForm.controls.FilterType.setValue(current === type ? null : type);
    this.audienceForm.controls.FilterType.markAsDirty();
  }

  isSelected(type: AudienceFilterType): boolean {
    return this.selectedFilter === type;
  }

  private isPreviewable(): boolean {
    if (!this.audienceForm) return false;
    const filterType = this.audienceForm.controls.FilterType.value;
    if (!filterType) return false;
    // AllConsumers has no config — always previewable
    if (filterType === AudienceFilterType.AllConsumers) return true;
    // WinBack — its config always has a valid default period
    if (filterType === AudienceFilterType.WinBack) {
      return this.audienceForm.controls.WinBackConfig?.valid ?? false;
    }
    // TopConsumers — require its config sub-group to be valid
    const configGroup = this.audienceForm.controls.TopConsumersConfig;
    return configGroup?.valid ?? false;
  }

  // Stable comparison key for the preview pipe that excludes IsDynamicAudience, so flipping
  // "Recalculate before sending" doesn't re-fire the audience-preview request.
  private previewKey(value: unknown): string {
    const { IsDynamicAudience, ...rest } = (value ?? {}) as Record<string, unknown>;
    return JSON.stringify(rest);
  }

  private buildPreviewRequest() {
    if (!this.audienceForm) return null;
    const filterType = this.audienceForm.controls.FilterType.value;
    if (!filterType) return null;
    const locationId = this.applicationProfileService.getApplicationProfile().Location.LocationId;
    let config: Record<string, unknown> = {};
    if (filterType === AudienceFilterType.TopConsumers) {
      config = this.audienceForm.controls.TopConsumersConfig.getRawValue();
    } else if (filterType === AudienceFilterType.WinBack) {
      config = this.audienceForm.controls.WinBackConfig.getRawValue();
    }
    return { locationId, filterType, config: config ?? {} };
  }
}
